import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { Crown, Lock, Clock, Zap, Bell, X, Eye, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Number of page views allowed before showing gate
const FREE_PAGE_VIEWS = 5;
// Key for localStorage
const PAGE_VIEWS_KEY = 'wt_page_views';
const GATE_SHOWN_KEY = 'wt_gate_shown';

const ContentGate = () => {
  const { user, token } = useAuth();
  const { hasFullAccess, isTrialActive, isTrialExpired, trialRemainingSeconds } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const [showGate, setShowGate] = useState(false);
  const [pageViews, setPageViews] = useState(0);
  const [gateType, setGateType] = useState('register'); // 'register' | 'expired' | 'subscribe'

  // Pages that should NOT trigger the gate
  const excludedPaths = [
    '/',
    '/login',
    '/register',
    '/subscribe',
    '/donate',
    '/admin',
  ];

  // Check if current path should be gated
  const isExcludedPath = excludedPaths.some(path => 
    location.pathname === path || location.pathname.startsWith('/admin')
  );

  useEffect(() => {
    // Don't track on excluded paths
    if (isExcludedPath) return;

    // If user has full access (premium or active trial), never show gate
    if (hasFullAccess) {
      setShowGate(false);
      return;
    }

    // If user is logged in but trial expired, show subscription gate
    if (user && isTrialExpired) {
      setGateType('expired');
      setShowGate(true);
      return;
    }

    // For non-logged-in users, track page views
    if (!user) {
      const stored = localStorage.getItem(PAGE_VIEWS_KEY);
      let views = [];
      
      if (stored) {
        try {
          const data = JSON.parse(stored);
          // Reset if data is older than 24 hours
          const timestamp = data.timestamp || 0;
          const now = Date.now();
          if (now - timestamp < 24 * 60 * 60 * 1000) {
            views = data.views || [];
          }
        } catch (e) {
          views = [];
        }
      }

      // Add current page if not already viewed
      const currentPath = location.pathname;
      if (!views.includes(currentPath)) {
        views.push(currentPath);
        localStorage.setItem(PAGE_VIEWS_KEY, JSON.stringify({
          views,
          timestamp: Date.now()
        }));
      }

      setPageViews(views.length);

      // Show gate after exceeding free views
      if (views.length > FREE_PAGE_VIEWS) {
        // Check if gate was dismissed in this session
        const dismissed = sessionStorage.getItem(GATE_SHOWN_KEY);
        if (!dismissed) {
          setGateType('register');
          setShowGate(true);
        }
      }
    }
  }, [location.pathname, user, hasFullAccess, isTrialExpired, isExcludedPath]);

  const handleDismiss = () => {
    setShowGate(false);
    // Allow dismissal once per session for register gate
    if (gateType === 'register') {
      sessionStorage.setItem(GATE_SHOWN_KEY, 'true');
    }
  };

  const handleRegister = () => {
    navigate('/register');
    setShowGate(false);
  };

  const handleSubscribe = () => {
    navigate('/subscribe');
    setShowGate(false);
  };

  const handleLogin = () => {
    navigate('/login');
    setShowGate(false);
  };

  // Format remaining trial time
  const formatTrialTime = (seconds) => {
    if (seconds <= 0) return 'Expired';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    }
    return `${minutes}m remaining`;
  };

  if (!showGate) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="content-gate">
      {/* Backdrop with blur */}
      <div 
        className="absolute inset-0 bg-slate-900/80 backdrop-blur-md"
        onClick={gateType === 'register' ? handleDismiss : undefined}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        {/* Header gradient */}
        <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 p-6 text-white text-center">
          {gateType === 'register' ? (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
                <Lock className="h-8 w-8" />
              </div>
              <h2 className="text-2xl font-bold mb-2">You've Reached Your Limit</h2>
              <p className="text-white/80 text-sm">
                You've viewed {pageViews} pages. Register for FREE to unlock full access.
              </p>
            </>
          ) : gateType === 'expired' ? (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
                <Clock className="h-8 w-8" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Your Free Trial Has Ended</h2>
              <p className="text-white/80 text-sm">
                Subscribe to continue enjoying unlimited access to The Watchtower.
              </p>
            </>
          ) : (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
                <Crown className="h-8 w-8" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Go Premium</h2>
              <p className="text-white/80 text-sm">
                Unlock all features and support independent journalism.
              </p>
            </>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {gateType === 'register' ? (
            <>
              {/* Free Trial Offer */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <Crown className="h-6 w-6 text-amber-600" />
                  <div>
                    <h3 className="font-bold text-slate-900">48 Hours FREE Access</h3>
                    <p className="text-xs text-slate-600">No credit card required</p>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <Eye className="h-4 w-4 text-emerald-600" />
                  </div>
                  <span className="text-sm text-slate-700">Unlimited page views & articles</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <Zap className="h-4 w-4 text-emerald-600" />
                  </div>
                  <span className="text-sm text-slate-700">AI-powered research assistant</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <Bell className="h-4 w-4 text-emerald-600" />
                  </div>
                  <span className="text-sm text-slate-700">SMS & email alerts</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <Button 
                onClick={handleRegister}
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white py-6 text-lg font-bold mb-3"
                data-testid="gate-register-btn"
              >
                Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <p className="text-center text-sm text-slate-500">
                Already have an account?{' '}
                <button onClick={handleLogin} className="text-emerald-600 font-medium hover:underline">
                  Log in
                </button>
              </p>

              {/* Dismiss option */}
              <button 
                onClick={handleDismiss}
                className="mt-4 w-full text-center text-xs text-slate-400 hover:text-slate-600"
              >
                Continue with limited access
              </button>
            </>
          ) : (
            <>
              {/* Subscription options for expired trial */}
              <div className="space-y-3 mb-6">
                <div className="border border-emerald-200 bg-emerald-50 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-bold text-slate-900">Monthly</h4>
                      <p className="text-xs text-slate-500">Cancel anytime</p>
                    </div>
                    <p className="text-2xl font-bold text-emerald-600">£3.99<span className="text-sm font-normal text-slate-500">/mo</span></p>
                  </div>
                </div>
                <div className="border border-slate-200 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-bold text-slate-900">Yearly</h4>
                      <p className="text-xs text-emerald-600">Save 20%</p>
                    </div>
                    <p className="text-2xl font-bold">£38.99<span className="text-sm font-normal text-slate-500">/yr</span></p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={handleSubscribe}
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white py-6 text-lg font-bold"
                data-testid="gate-subscribe-btn"
              >
                Subscribe Now
              </Button>

              <p className="text-center text-xs text-slate-500 mt-4">
                Support independent political journalism
              </p>
            </>
          )}
        </div>

        {/* Close button for register gate */}
        {gateType === 'register' && (
          <button 
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ContentGate;
